import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import type { MatchHistoryPlayer, MatchHistoryRow } from '../lib/api.js';
import { useAuth } from '../stores/auth.js';
import { ChipIcon } from "../components/ChipIcon.js";

type Outcome = 'win' | 'loss' | 'draw' | 'pending';

function outcomeOf(match: MatchHistoryRow, myId: string): Outcome {
  if (match.isDraw) return 'draw';
  if (match.loserId === myId) return 'loss';
  if (match.loserId !== null) return 'win';
  return 'pending';
}

const OUTCOME_LABEL: Record<Outcome, string> = {
  win: 'Победа',
  loss: 'Поражение',
  draw: 'Ничья',
  pending: 'В процессе',
};

const OUTCOME_COLOR: Record<Outcome, string> = {
  win: 'var(--color-gold)',
  loss: 'var(--color-coral)',
  draw: 'var(--color-ivory)',
  pending: 'var(--color-ivory)',
};

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function Avatar({ player, dim }: { player: MatchHistoryPlayer; dim?: boolean }) {
  const initial = player.firstName.slice(0, 1).toUpperCase();
  const opacity = dim ? 0.55 : 1;
  return (
    <div
      className="grid h-7 w-7 place-items-center overflow-hidden rounded-full"
      style={{
        border: '1px solid var(--color-gold)',
        opacity,
        background: 'var(--color-felt-deep)',
      }}
      title={player.username ? `@${player.username}` : player.firstName}
    >
      {player.photoUrl ? (
        <img src={player.photoUrl} alt={player.firstName} className="h-full w-full object-cover" />
      ) : (
        <span className="text-xs text-gold" style={{ fontFamily: 'var(--font-display)' }}>
          {initial}
        </span>
      )}
    </div>
  );
}

export function History() {
  const me = useAuth((s) => s.user);
  const [items, setItems] = useState<MatchHistoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api
      .matchHistory()
      .then((rows) => {
        if (cancelled) return;
        setItems(rows);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'не удалось загрузить');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto flex w-full max-w-[420px] flex-col gap-4 px-5 py-6">
      <h1 className="heading-display text-3xl">История</h1>
      {error && (
        <p className="rounded-2xl px-4 py-3 text-sm text-coral" style={{ background: 'var(--color-glass)', border: '1px solid color-mix(in oklab, var(--color-coral) 35%, transparent)' }}>
          {error}
        </p>
      )}
      {items === null && !error && <p className="text-sm text-ivory/60">Загружаем…</p>}
      {items && items.length === 0 && (
        <p className="text-sm text-ivory/60">Пока нет завершённых партий. Сыграй первую — она появится здесь.</p>
      )}
      {items && items.length > 0 && me && (
        <ul className="flex flex-col gap-2">
          {items.map((m) => {
            const outcome = outcomeOf(m, me.id);
            const opponents = m.players.filter((p) => p.userId !== me.id);
            const delta = m.myDeltaStars;
            const deltaColor =
              delta > 0 ? 'var(--color-gold)' : delta < 0 ? 'var(--color-coral)' : 'var(--color-ivory)';
            const deltaLabel = delta > 0 ? `+${delta}` : delta < 0 ? `${delta}` : '0';
            return (
              <li key={m.id}>
                <Link
                  to={`/history/${m.id}`}
                  className="flex items-center justify-between gap-3 rounded-2xl px-4 py-3 transition-colors hover:bg-white/[0.04]"
                  style={{
                    background: 'var(--color-glass)',
                    border: '1px solid color-mix(in oklab, var(--color-gold) 30%, transparent)',
                  }}
                >
                <div className="flex min-w-0 flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: OUTCOME_COLOR[outcome] }}>
                      {OUTCOME_LABEL[outcome]}
                    </span>
                    <span className="text-xs text-ivory/55">· {formatDate(m.finishedAt)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {opponents.slice(0, 5).map((p) => (
                      <Avatar key={p.userId} player={p} dim={p.userId === m.loserId && outcome !== 'win'} />
                    ))}
                    {opponents.length > 5 && (
                      <span className="text-xs text-ivory/55">+{opponents.length - 5}</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {m.stakeStars > 0 ? (
                    <span
                      className="flex items-center gap-1.5"
                      style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: deltaColor }}
                    >
                      {deltaLabel}
                      <ChipIcon size={16} />
                    </span>
                  ) : (
                    <span className="text-xs text-ivory/55">Free</span>
                  )}
                  {m.stakeStars > 0 && (
                    <span className="text-[10px] uppercase tracking-wider text-ivory/40">
                      ставка {m.stakeStars}
                    </span>
                  )}
                </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
